import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileCompletionPrompt } from '@/components/ProfileCompletionPrompt';
import { SocialMediaLinksTab } from '@/components/profile/SocialMediaLinksTab';
import {
  Building2,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  Target,
  Briefcase,
  UserCheck,
  Mail,
  Globe,
  PieChart,
  Users,
  Edit,
  User,
  Settings
} from 'lucide-react';
import { useVentureData } from '@/hooks/useVentureData';
import { useUser } from '@/contexts/UserContext';
import { useUserRole } from '@/hooks/useUserRole';
import VCProfileEditDrawer from './VCProfileEditDrawer';
import { userService } from '@/services/userService';
import { AdminProfileSection } from '@/components/admin/AdminProfileSection';

const VCProfile: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUserData } = useUser();
  const { isAdmin } = useUserRole();
  const { ventureData, loading, error } = useVentureData();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editSection, setEditSection] = useState<'overview' | 'firm' | 'investment' | 'contact'>('overview');

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="h-20 w-20 rounded-full bg-muted animate-pulse" />
              <div className="space-y-2">
                <div className="h-6 w-48 bg-muted animate-pulse rounded" />
                <div className="h-4 w-32 bg-muted animate-pulse rounded" />
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 bg-muted animate-pulse rounded" />
          <div className="h-64 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error || !ventureData) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center space-y-4">
            <p className="text-muted-foreground">
              {error || 'Unable to load venture capital profile. Please try again.'}
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!ventureData.venture_capital) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              Venture capital profile data not found. Please complete your VC profile setup.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const vc = ventureData.venture_capital;
  const profile = ventureData;

  const handleEditClick = (section: 'overview' | 'firm' | 'investment' | 'contact') => {
    setEditSection(section);
    setEditDrawerOpen(true);
  };

  const handleSave = async (updatedData: any) => {
    try {
      await userService.updateProfile(profile.id, updatedData);
      await refreshUserData();
    } catch (error) {
      throw error;
    }
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(1)}B`;
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount}`;
  };

  return (
    <div className="space-y-6">
      <ProfileCompletionPrompt />
      
      {/* Admin Section */}
      {isAdmin && <AdminProfileSection />}
      
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="text-lg">
                  {profile.display_name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <Button
                variant="outline"
                size="icon"
                className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full shadow-md bg-background"
                onClick={() => navigate('/settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {vc.firm_name || profile.display_name}
                  </h1>
                  <p className="text-muted-foreground">
                    Partner at {vc.firm_name || 'Venture Capital Firm'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {profile.is_verified && (
                    <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                      <UserCheck className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                  <Badge variant="outline">
                    Venture Capital
                  </Badge>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                {profile.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {profile.location}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Joined {new Date(profile.created_at).toLocaleDateString()}
                </div>
                {vc.aum && (
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4" />
                    {formatCurrency(Number(vc.aum))} AUM
                  </div>
                )}
              </div>

              {profile.bio && (
                <p className="mt-4 text-foreground leading-relaxed">
                  {profile.bio}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <DollarSign className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-foreground">
              {vc.aum ? formatCurrency(Number(vc.aum)) : '$0'}
            </div>
            <div className="text-sm text-muted-foreground">Assets Under Management</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Building2 className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-foreground">
              {vc.portfolio_companies || 0}
            </div>
            <div className="text-sm text-muted-foreground">Portfolio Companies</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-foreground">
              {vc.total_investments ? formatCurrency(Number(vc.total_investments)) : '$0'}
            </div>
            <div className="text-sm text-muted-foreground">Total Investments</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Target className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-foreground">
              {vc.ticket_size_min && vc.ticket_size_max ? 
                `${formatCurrency(Number(vc.ticket_size_min))}-${formatCurrency(Number(vc.ticket_size_max))}` : 
                'N/A'
              }
            </div>
            <div className="text-sm text-muted-foreground">Ticket Size</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabbed Profile Content */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-5">
          <TabsTrigger value="overview" className="whitespace-nowrap text-xs md:text-sm">Overview</TabsTrigger>
          <TabsTrigger value="firm" className="whitespace-nowrap text-xs md:text-sm">Firm Details</TabsTrigger>
          <TabsTrigger value="investment" className="whitespace-nowrap text-xs md:text-sm">Investment</TabsTrigger>
          <TabsTrigger value="contact" className="whitespace-nowrap text-xs md:text-sm">Contact Info</TabsTrigger>
          <TabsTrigger value="social" className="whitespace-nowrap text-xs md:text-sm">Social Media</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Profile Overview
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('overview')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Display Name</span>
                <span className="font-medium">{profile.display_name || 'Not provided'}</span>
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-start">
                <span className="text-sm text-muted-foreground sm:flex-shrink-0">Bio</span>
                <span className="font-medium sm:text-right sm:max-w-xs sm:truncate">{profile.bio || 'Not provided'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Location</span>
                <span className="font-medium">{profile.location || 'Not provided'}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="firm" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Firm Details
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('firm')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Firm Name</span>
                <span className="font-medium">{vc.firm_name || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Partner Name</span>
                <span className="font-medium">{vc.partner_name || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Partner Title</span>
                <span className="font-medium">{vc.partner_title || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">AUM</span>
                <span className="font-medium">{vc.aum ? formatCurrency(Number(vc.aum)) : 'Not disclosed'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Portfolio Size</span>
                <span className="font-medium">{vc.portfolio_companies || 0} companies</span>
              </div>
              {vc.investment_focus && vc.investment_focus.length > 0 && (
                <div>
                  <span className="text-sm text-muted-foreground block mb-2">Investment Focus</span>
                  <div className="flex flex-wrap gap-2">
                    {vc.investment_focus.map((focus: string, index: number) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {focus}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="investment" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Investment Criteria
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('investment')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Min Ticket Size</span>
                <span className="font-medium">{vc.ticket_size_min ? formatCurrency(Number(vc.ticket_size_min)) : 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Max Ticket Size</span>
                <span className="font-medium">{vc.ticket_size_max ? formatCurrency(Number(vc.ticket_size_max)) : 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Investment Thesis</span>
                <span className="font-medium text-right max-w-xs">{vc.investment_thesis || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Decision Timeline</span>
                <span className="font-medium">{vc.decision_timeline || 'Not specified'}</span>
              </div>
              {vc.stage_preference && vc.stage_preference.length > 0 && (
                <div>
                  <span className="text-sm text-muted-foreground block mb-2">Stage Preference</span>
                  <div className="flex flex-wrap gap-2">
                    {vc.stage_preference.map((stage: string, index: number) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {stage}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Contact Information
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('contact')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="font-medium">{profile.email}</span>
              </div>
              {vc.partner_email && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Partner Email</span>
                  <span className="font-medium">{vc.partner_email}</span>
                </div>
              )}
              {vc.partner_phone && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Partner Phone</span>
                  <span className="font-medium">{vc.partner_phone}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Account Type</span>
                <Badge variant="outline">{profile.account_type}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Profile Status</span>
                <Badge variant={profile.setup_completed ? "default" : "secondary"}>
                  {profile.setup_completed ? 'Complete' : 'Incomplete'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="social" className="space-y-4">
          <SocialMediaLinksTab />
        </TabsContent>
      </Tabs>

      <VCProfileEditDrawer
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        section={editSection}
        data={ventureData}
        onSave={handleSave}
      />
    </div>
  );
};

export default VCProfile;